/**
 * API Utility Functions
 * Backend API ile iletişim için helper fonksiyonlar
 */

// Production'da relative path kullan, development'ta localhost
const isProduction = import.meta.env.PROD || window.location.hostname !== 'localhost';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (isProduction ? '/api' : 'http://localhost:3000/api');
const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL || (isProduction ? '' : 'http://localhost:3000');

/**
 * Cihaz bazlı benzersiz ID oluşturur
 * WiFi ortamında farklı cihazların ayrı ayrı oy kullanabilmesi için
 */
const getOrCreateClientId = () => {
    const storageKey = 'nox_client_id';
    let clientId = localStorage.getItem(storageKey);
    
    if (!clientId) {
        // Benzersiz ID oluştur: timestamp + random + fingerprint
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 15);
        const fingerprint = generateFingerprint();
        clientId = `${timestamp}_${random}_${fingerprint}`;
        localStorage.setItem(storageKey, clientId);
    }
    
    return clientId;
};

/**
 * Basit cihaz fingerprint oluşturur
 */
const generateFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Nox', 0, 0);
    
    const data = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        screen.colorDepth,
        new Date().getTimezoneOffset(),
        canvas.toDataURL().slice(-50) // Canvas fingerprint
    ].join('|');
    
    // Simple hash
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
        hash = ((hash << 5) - hash) + data.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
};

/**
 * API request helper
 */
export async function apiRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'X-Client-Id': getOrCreateClientId(), // Cihaz bazlı ID
            ...options.headers
        },
        ...options,
        credentials: 'include', // Include credentials for CORS
        mode: 'cors' // Enable CORS
    };

    // Add auth token if available (halka açık uçlarda gönderme — 401 yönlendirmesi portal aramasını bozmasın)
    const token = localStorage.getItem('token');
    const skipAuth =
        endpoint.includes('/public/') ||
        endpoint.includes('/menu/search/organizations');
    if (token && !skipAuth) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    try {
        // Support both 'data' and 'body' options (data is more common in axios-style APIs)
        const requestData = options.data || options.body;
        
        // Handle FormData separately (don't stringify)
        if (requestData instanceof FormData) {
            delete config.headers['Content-Type']; // Let browser set it with boundary
            config.body = requestData;
        } else if (requestData && typeof requestData === 'object' && !(requestData instanceof FormData)) {
            config.body = JSON.stringify(requestData);
        } else if (requestData) {
            config.body = requestData;
        }

        const response = await fetch(url, config);
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        let data;
        
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error(text || 'API request failed');
            }
        }

        if (!response.ok) {
            // Handle 401 Unauthorized
            if (response.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                const isZpanel = typeof window !== 'undefined' && window.location.pathname?.startsWith('/zpanel');
                window.location.href = isZpanel ? '/zpanel/login' : '/admin/login';
            }
            
            // Menü slug 404: son kullanıcıya slug/admin detayı ekleme (MenuPage ayrı ekran gösterir)
            if (response.status === 404) {
                const errorMsg = data.message || data.error || 'Bulunamadı';
                if (url.includes('/menu/slug/')) {
                    const err = new Error(errorMsg);
                    err.code = 'RESTAURANT_NOT_FOUND';
                    throw err;
                }
                throw new Error(errorMsg);
            }
            
            // Handle validation errors with detailed messages
            if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
                const errorMessages = data.messages.map(msg => `${msg.field}: ${msg.message}`).join(', ');
                throw new Error(errorMessages);
            }
            
            throw new Error(data.message || data.error || 'API request failed');
        }

        return data;
    } catch (error) {
        console.error('API Error:', error);
        console.error('URL:', url);
        console.error('Config:', config);
        
        // More detailed error message
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            throw new Error('Backend sunucusuna bağlanılamıyor. Lütfen backend\'in çalıştığından emin olun.');
        }
        
        throw error;
    }
}

/**
 * Get menu by organization slug
 * Cache-busting with timestamp to ensure fresh data
 */
/** Ana sayfa portalı: aktif organizasyon araması (min. 2 karakter) */
export const searchPortalOrganizations = async (q, limit = 20) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 2) {
        return { success: true, data: [] };
    }
    const res = await apiRequest(`/menu/search/organizations?q=${encodeURIComponent(trimmed)}&limit=${limit}`);
    const raw = res?.data;
    const data = Array.isArray(raw) ? raw : [];
    return { ...res, data };
};

export const getMenuBySlug = async (slug, branchId = null, forceRefresh = false) => {
    const timestamp = forceRefresh ? `&_t=${Date.now()}` : `&_t=${Math.floor(Date.now() / 5000)}`; // 5 second cache groups
    const params = branchId ? `?branch_id=${branchId}${timestamp}` : `?_t=${timestamp.substring(3)}`;
    return apiRequest(`/menu/slug/${slug}${params}`);
};

/**
 * Get menu by organization ID
 * Cache-busting with timestamp to ensure fresh data
 */
export const getMenuById = async (organizationId, branchId = null, forceRefresh = false) => {
    const timestamp = forceRefresh ? `&_t=${Date.now()}` : `&_t=${Math.floor(Date.now() / 5000)}`;
    const params = branchId ? `?branch_id=${branchId}${timestamp}` : `?_t=${timestamp.substring(3)}`;
    return apiRequest(`/menu/${organizationId}${params}`);
};

/**
 * Cast vote
 */
export const castVote = async (organizationId, voteId, voteOptionId) => {
    return apiRequest(`/organizations/${organizationId}/votes/public/${voteId}/cast`, {
        method: 'POST',
        body: JSON.stringify({ vote_option_id: voteOptionId })
    });
};

/**
 * Toggle like (legacy)
 */
export const toggleLike = async (productId) => {
    return apiRequest(`/likes/products/${productId}/toggle`, {
        method: 'POST'
    });
};

/**
 * Set product reaction (like or dislike)
 * @param {number} productId - Product ID
 * @param {string} type - 'like' or 'dislike'
 */
export const setProductReaction = async (productId, type) => {
    return apiRequest(`/likes/products/${productId}/reaction`, {
        method: 'POST',
        body: JSON.stringify({ type }),
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

/**
 * Get product reaction status
 * @param {number} productId - Product ID
 */
export const getProductReaction = async (productId) => {
    return apiRequest(`/likes/products/${productId}/check`);
};

/**
 * Track hit (analytics)
 */
export const trackHit = async (organizationId, entityType, entityId = 0) => {
    try {
        return await apiRequest('/analytics/track', {
            method: 'POST',
            data: {
                organization_id: organizationId,
                entity_type: entityType,
                entity_id: entityId || 0
            }
        });
    } catch (error) {
        console.debug('Track hit error:', error);
        // Don't throw, just log - tracking shouldn't break the app
        return { success: false, error };
    }
};

/**
 * Send contact message
 */
export const sendContactMessage = async (organizationId, messageData) => {
    return apiRequest(`/organizations/${organizationId}/contact`, {
        method: 'POST',
        body: JSON.stringify({
            organization_id: organizationId,
            ...messageData
        })
    });
};

/**
 * Get full image URL
 * Converts relative image paths to full backend URLs
 */
export const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    // If starts with /uploads, use relative path in production (same domain)
    // or prepend backend URL in development
    if (imagePath.startsWith('/uploads/')) {
        // In production, use relative path (same domain, no need for full URL)
        // In development, prepend backend URL
        if (isProduction) {
            return imagePath; // Relative path works in production
        }
        return `${BACKEND_BASE_URL}${imagePath}`;
    }
    
    // Otherwise return as is (might be a data URL or other format)
    return imagePath;
};

export default {
    getMenuBySlug,
    getMenuById,
    castVote,
    toggleLike,
    trackHit,
    sendContactMessage,
    getImageUrl
};

